import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import React, { useState, useEffect, useCallback } from 'react';
import { fileService, ApiFile, GetFilesParams } from '../../services/file.service';
import {
    Avatar,
    Box,
    CircularProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    TextField,
    Typography,
    Grid,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    SelectChangeEvent
} from '@mui/material';
import { Search as SearchIcon, Description as DescriptionIcon } from '@mui/icons-material';
import { PageHint } from '../common/PageHint';
import { UniversalPagination } from '../common/UniversalPagination';
import { toastService } from '../../services/toast.service';

interface ClientFilesTabProps {
    clientId: string;
}

export const ClientFilesTab: React.FC<ClientFilesTabProps> = ({ clientId }) => {
    const [files, setFiles] = useState<ApiFile[]>([]);
    const [totalFiles, setTotalFiles] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [fileTypeFilter, setFileTypeFilter] = useState<'image' | 'document' | 'other' | ''>('');

    const fetchFiles = useCallback(async () => {
        setLoading(true);
        try {
            const params: GetFilesParams = {
                limit,
                offset: (page - 1) * limit,
                search: searchQuery || undefined,
                type: fileTypeFilter || undefined,
            };
            const response = await fileService.getClientFiles(clientId, params);
            setFiles(response.files);
            setTotalFiles(response.total);
        } catch (err) {
            toastService.error('Не вдалося завантажити файли клієнта.');
            console.error('Fetch client files error:', err);
        } finally {
            setLoading(false);
        }
    }, [clientId, page, limit, searchQuery, fileTypeFilter]);

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    const handleDownload = (file: ApiFile) => {
        try {
            const link = document.createElement('a');
            link.href = file.url;
            link.setAttribute('download', file.originalFilename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            toastService.error('Не вдалося завантажити файл.');
            console.error('Download error:', err);
        }
    };

    const handlePageChange = (value: number) => {
        setPage(value);
    };

    const handleLimitChange = (value: number) => {
        setLimit(value);
        setPage(1);
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
        setPage(1);
    };

    const handleFilterChange = (event: SelectChangeEvent<string>) => {
        setFileTypeFilter(event.target.value as any);
        setPage(1);
    };

    return (
        <Box data-i-doc={PageIndices.EXPERT_CLIENT_FILES}>
            <PageHint iDoc={PageIndices.EXPERT_CLIENT_FILES} />
            <Typography variant="h6" gutterBottom>Файли клієнта</Typography>
            <Paper sx={{ p: 2, mt: 2 }}>
                <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
                    <Grid item xs={12} sm={8}>
                         <TextField
                            fullWidth
                            label="Пошук за назвою"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel>Тип файлу</InputLabel>
                            <Select value={fileTypeFilter} label="Тип файлу" onChange={handleFilterChange}>
                                <MenuItem value=""><em>Всі</em></MenuItem>
                                <MenuItem value="image">Зображення</MenuItem>
                                <MenuItem value="document">Документи</MenuItem>
                                <MenuItem value="other">Інші</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {loading && (!files || files.length === 0) ? (
                    <Box display="flex" justifyContent="center" sx={{ mt: 4 }}><CircularProgress /></Box>
                ) : (!files || files.length === 0) ? (
                     <Typography sx={{ mt: 4, textAlign: 'center' }}>Файлів не знайдено.</Typography>
                ) : (
                    <List>
                        {files.map((file) => (
                            <ListItem key={file.id} divider disablePadding>
                                <ListItemButton onClick={() => handleDownload(file)}>
                                    <ListItemIcon>
                                        {file.fileType === 'image' ? (
                                            <Avatar variant="square" src={file.url} />
                                        ) : (
                                            <Avatar variant="square"><DescriptionIcon /></Avatar>
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={file.originalFilename}
                                        secondary={
                                            <>
                                                <Typography component="span" variant="body2" color="text.primary">
                                                    {`${file.fileType} - ${(file.size / 1024).toFixed(2)} KB`}
                                                </Typography>
                                                {file.comment && (
                                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                                        {file.comment}
                                                    </Typography>
                                                )}
                                            </>
                                        }
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}

                <UniversalPagination
                    page={page}
                    count={Math.ceil(totalFiles / limit)}
                    limit={limit}
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    storageKey={`pagination:client-files-${clientId}`}
                />
            </Paper>
        </Box>
    );
};
